/* eslint-disable max-len */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import './UserHome.scss';
import { Container, Row, Col, Card, Button, Nav, Tab } from 'react-bootstrap';
import { connect } from 'react-redux';
import { accountDetailsRequestAction } from '../../store/account/userActions';
import { useHistory } from 'react-router-dom';
import btcusdt from '../../assets/img/coin/btcusdt.png';
import ethusdt from '../../assets/img/coin/ethusdt.png';
import bnbusdt from '../../assets/img/coin/bnbusdt.png';
import xrpusdt from '../../assets/img/coin/xrpusdt.png';
import solusdt from '../../assets/img/coin/solusdt.png';
import xml2js from 'xml2js';
import PnlHistoryChart from '../../components/PnlHistoryChart/PnlHistoryChart';
import {
  getPnlHistoryRequestAction,
} from '../../store/bot/botActions';
import {
  getNewsRequestAction
} from '../../store/news/newsActions';

import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Activity,
  Users,
  Key,
  Globe,
  Bell,
  BarChart3,
  Settings,
  DollarSign
} from 'lucide-react';

function UserHome({ ...props }) {
  const { user, accountDetailsRequest, getPnlHistoryRequestAction, getNewsRequest, newsData } = props;
  const history = useHistory();

  const [popularTokens, setPopularTokens] = useState([]);
  const [news, setNews] = useState([]);
  const [summary, setSummary] = useState({ totalPnl: 0, totalPnlPercent: 0 });
  
  const wsConnectionsRef = useRef({});
  const intervalsRef = useRef({});
  const isMountedRef = useRef(true);

  const handleTotalPnlChange = useCallback((data) => {
    setSummary(data);
  }, []);

  const getTokenImage = useCallback((symbol) => {
    const sym = symbol?.toLowerCase();
    switch (sym) {
      case 'btcusdt':
      case 'btc':
        return btcusdt;
      case 'ethusdt':
      case 'eth':
        return ethusdt;
      case 'bnbusdt':
      case 'bnb':
        return bnbusdt;
      case 'xrpusdt':
      case 'xrp':
        return xrpusdt;
      case 'solusdt':
      case 'sol':
        return solusdt;
      default:
        return null;
    }
  }, []);

  const throttledUpdateRef = useRef(null);
  const updateQueue = useRef([]);

  const processUpdateQueue = useCallback(() => {
    if (updateQueue.current.length === 0) return;

    const updates = [...updateQueue.current];
    updateQueue.current = [];

    setPopularTokens(prevTokens => {
      const newTokens = [...prevTokens];
      
      updates.forEach(({ update, sourceName }) => {
        if (!Array.isArray(update)) {
          update = [update];
        }

        update.forEach(item => {
          if (!item || !item.symbol) return;

          const tokenIndex = newTokens.findIndex(token => token.symbol === item.symbol);
          if (tokenIndex !== -1) {
            const token = newTokens[tokenIndex];
            const newPrice = item.price !== null ? item.price : token.price;
            const newQuantity = item.quantity !== null ? item.quantity : token.quantity;

            if (newPrice !== token.price || newQuantity !== token.quantity) {
              newTokens[tokenIndex] = {
                ...token,
                price: newPrice,
                quantity: newQuantity,
                lastUpdated: Date.now(),
                source: sourceName
              };
            }
          }
        });
      });

      return newTokens;
    });
  }, []);

  const handleUpdate = useCallback((update, sourceName) => {
    if (!isMountedRef.current || !update) return;

    updateQueue.current.push({ update, sourceName });

    if (throttledUpdateRef.current) {
      clearTimeout(throttledUpdateRef.current);
    }

    throttledUpdateRef.current = setTimeout(processUpdateQueue, 100);
  }, [processUpdateQueue]);

  const connectWebSocket = useCallback((source) => {
    if (wsConnectionsRef.current[source.name]) {
      return;
    }

    try {
      const ws = new WebSocket(source.url);
      
      ws.onopen = () => {
        console.log(`Connected to ${source.name}`);
      };

      ws.onmessage = (event) => {
        const update = source.processData(event.data);
        if (update) {
          handleUpdate(update, source.name);
        }
      };

      ws.onerror = (error) => {
        console.error(`WebSocket error on ${source.name}:`, error);
      };

      ws.onclose = () => {
        delete wsConnectionsRef.current[source.name];
        
        if (isMountedRef.current) {
          setTimeout(() => {
            if (isMountedRef.current) {
              connectWebSocket(source);
            }
          }, 5000);
        }
      };

      wsConnectionsRef.current[source.name] = ws;
    } catch (error) {
      console.error(`Failed to connect to ${source.name}:`, error);
    }
  }, [handleUpdate]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    const coins = ['btcusdt', 'ethusdt', 'bnbusdt', 'xrpusdt', 'solusdt'];
    setPopularTokens(coins.map(coin => ({
      symbol: coin.slice(0, -4).toUpperCase(),
      price: '0',
      quantity: '0',
      lastUpdated: Date.now()
    })));

    const primarySource = {
      name: 'binance-ws',
      url: `wss://stream.binance.com:9443/stream?streams=${coins.map(coin => `${coin}@ticker`).join('/')}`,
      processData: (data) => {
        try {
          const parsedData = JSON.parse(data);
          if (parsedData.data) {
            const coinSymbol = parsedData.data.s.toLowerCase();
            return {
              symbol: coinSymbol.slice(0, -4).toUpperCase(),
              price: parsedData.data.c,
              quantity: parsedData.data.P
            };
          }
        } catch (e) {
          console.error("Error processing Binance data:", e);
        }
        return null;
      }
    };

    connectWebSocket(primarySource);

    return () => {
      Object.values(wsConnectionsRef.current).forEach(ws => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      });
      wsConnectionsRef.current = {};
      
      Object.values(intervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
      intervalsRef.current = {};
      
      if (throttledUpdateRef.current) {
        clearTimeout(throttledUpdateRef.current);
      }
    };
  }, [connectWebSocket]);

  const memoizedAccountDetailsRequest = useCallback(() => {
    accountDetailsRequest();
  }, [accountDetailsRequest]);

  useEffect(() => {
    getPnlHistoryRequestAction("1_day");
    memoizedAccountDetailsRequest();
    getNewsRequest();
  }, [getPnlHistoryRequestAction, memoizedAccountDetailsRequest, getNewsRequest]);

  useEffect(() => {
    if (!newsData) return;

    try {
      const rssString = typeof newsData === 'object' ? Object.values(newsData).join('') : newsData;
      const parser = new xml2js.Parser();
      
      parser.parseString(rssString, (err, result) => {
        if (err || !result?.rss?.channel?.[0]?.item) return;
        
        const latestNews = result.rss.channel[0].item.slice(0, 5);
        setNews(latestNews);
      });
    } catch (error) {
      console.error('Error processing news:', error);
    }
  }, [newsData]);

  const totalBalance = useMemo(() => {
    return (user?.binance_balance || 0) + (user?.okx_balance || 0);
  }, [user?.binance_balance, user?.okx_balance]);

  const apiConnected = useMemo(() => {
    return user?.binance || user?.okx;
  }, [user?.binance, user?.okx]);

  const redirectApiKey = useCallback(() => {
    history.push('/general-settings/api-settings');
  }, [history]);

  const goToProfile = useCallback(() => {
    history.push('/profile');
  }, [history]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Modern StatCard Component
  const StatCard = React.memo(({ icon: Icon, title, value, subtitle, color = 'primary', trend, className = '' }) => (
    <div className={`modern-stat-card ${className}`}>
      <div className="stat-card-body">
        <div className={`stat-icon stat-icon-${color}`}>
          <Icon size={24} />
        </div>
        <div className="stat-content">
          <h6 className="stat-title">{title}</h6>
          <h4 className="stat-value">{value}</h4>
          {subtitle && <small className="stat-subtitle">{subtitle}</small>}
          {trend && (
            <div className="stat-trend">
              {trend > 0 ? (
                <TrendingUp size={16} className="trend-up" />
              ) : (
                <TrendingDown size={16} className="trend-down" />
              )}
              <small className={trend > 0 ? 'trend-up' : 'trend-down'}>
                {Math.abs(trend)}%
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  ));

  const ModernBadge = React.memo(({ children, variant = 'primary', pulse = false }) => (
    <span className={`modern-badge modern-badge-${variant} ${pulse ? 'pulse-animation' : ''}`}>
      {children}
    </span>
  ));

  return (
    <div className="modern-dashboard">
      <Container fluid>
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-content">
            <div className="header-left">
              <div className="header-text">
                <h1 className="dashboard-title">Welcome to Multisignals</h1>
                <div className="user-greeting">
                  <h3 className="user-name">{user?.full_name || 'Guest'}</h3>
                  <p className="user-email">{user?.email || 'guest@example.com'}</p>
                </div>
              </div>
            </div>
            <div className="header-right">
              <Button variant="outline-primary" size="sm" className="modern-btn" onClick={goToProfile}>
                <Settings size={16} className="me-1" />
                Settings
              </Button>
              <Button variant="primary" size="sm" className="modern-btn">
                <Bell size={16} className="me-1" />
                Notifications
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <StatCard
              icon={Wallet}
              title="Available Usdt Balance"
              value={`$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              subtitle="Across all exchanges"
              color="success"
            />
          </Col>
          <Col lg={3} md={6}>
            <StatCard
              icon={Activity}
              title="Active Bots"
              value={user?.registered_bots || 0}
              subtitle="Currently running"
              color="primary"
            />
          </Col>
          <Col lg={3} md={6}>
            <StatCard
              icon={summary.totalPnl > 0 ? TrendingUp : TrendingDown}
              title="Today's P&L"
              value={`$${summary.totalPnl.toFixed(2)}`}
              subtitle="Last day"
              color={summary.totalPnl > 0 ? "success" : "danger"}
            />
          </Col>
          <Col lg={3} md={6}>
            <StatCard
              icon={Users}
              title="Win Rate"
              value="73.5%"
              subtitle="This month"
              color="info"
              trend={2.1}
            />
          </Col>
        </Row>

        {/* API Status */}
        <Row className="g-4 mb-4">
          <Col md={12}>
            <Card className="modern-card modern-api-card">
              <Card.Body>
                <div className="card-header-modern">
                  <h5 className="card-title-modern">Exchange Connections</h5>
                  <ModernBadge variant={apiConnected ? 'success' : 'warning'}>
                    {apiConnected ? 'Connected' : 'Setup Required'}
                  </ModernBadge>
                </div>

                <div className="exchange-status-modern">
                  <Row className="g-3">
                    <Col md={3}>
                      <div className="exchange-item-modern">
                        <div className="exchange-info-modern">
                          <div className={`status-dot-modern ${user?.binance ? 'active' : 'inactive'}`}></div>
                          <span className="exchange-name-modern">Binance API Key Status</span>
                        </div>
                        <div className="exchange-actions-modern">
                          {user?.binance ? (
                            <ModernBadge variant="success">Active</ModernBadge>
                          ) : (
                            <Button className="modern-connect-btn" onClick={redirectApiKey}>
                              <Key size={14} className="me-1" />
                              Add API Key
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={3}>
                      <div className="exchange-item-modern">
                        <div className="exchange-info-modern">
                          <div className={`status-dot-modern ${user?.okx ? 'active' : 'inactive'}`}></div>
                          <span className="exchange-name-modern">OKX API Key Status</span>
                        </div>
                        <div className="exchange-actions-modern">
                          {user?.okx ? (
                            <ModernBadge variant="success">Active</ModernBadge>
                          ) : (
                            <Button className="modern-connect-btn" onClick={redirectApiKey}>
                              <Key size={14} className="me-1" />
                              Add API Key
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={3}>
                      <div className="exchange-item-modern">
                        <div className="exchange-info-modern">
                          <DollarSign size={16} className="me-2 text-success" />
                          <span className="exchange-name-modern">USDT Balance</span>
                        </div>
                        <div className="exchange-actions-modern">
                          {user?.binance || user?.okx ? (
                            <div className="balance-display-modern">
                              {user?.binance && (
                                <div className="balance-item-modern">
                                  <small>Binance:</small>
                                  <span>${Number(user?.binance_balance || 0).toFixed(2)}</span>
                                </div>
                              )}
                              {user?.okx && (
                                <div className="balance-item-modern">
                                  <small>OKX:</small>
                                  <span>${Number(user?.okx_balance || 0).toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <Button className="modern-connect-btn" onClick={redirectApiKey}>
                              <Key size={14} className="me-1" />
                              Add API Key
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={3}>
                      <div className="exchange-item-modern">
                        <div className="exchange-info-modern">
                          <Activity size={16} className="me-2 text-primary" />
                          <span className="exchange-name-modern">Registered Bots</span>
                        </div>
                        <div className="exchange-actions-modern">
                          {user?.registered_bots ? (
                            <div className="bot-count-modern">{user?.registered_bots}</div>
                          ) : (
                            <Button className="modern-connect-btn" onClick={redirectApiKey}>
                              <Key size={14} className="me-1" />
                              Add API Key
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {!apiConnected && (
                  <div className="modern-alert">
                    <strong>Setup Required:</strong> Connect your exchange API to start trading with bots.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Market Data and News */}
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <Card className="modern-card modern-market-card" style={{minHeight: '500px'}}>
              <Card.Body>
                <div className="card-header-modern">
                  <h5 className="card-title-modern">
                    <BarChart3 size={20} className="me-2" />
                    Cryptocurrency Market
                  </h5>
                  <ModernBadge variant="success" pulse>Live</ModernBadge>
                </div>

                <div className="modern-token-display">
                  <Tab.Container id="market-tabs" defaultActiveKey="popular">
                    <Nav variant="tabs" className="modern-nav-tabs">
                      <strong className="tab-title">Popular Tokens</strong>
                    </Nav>
                    <Tab.Content>
                      <Tab.Pane eventKey="popular">
                        <div className="modern-token-list">
                          {popularTokens.map((token, index) => (
                            <div key={index} className="modern-token-item">
                              <div className="token-left">
                                <img
                                  src={getTokenImage(token?.symbol)}
                                  alt={token?.symbol}
                                  className="token-image"
                                />
                                <div className="token-info">
                                  <div className="token-name">{token?.symbol}</div>
                                  <small className="token-full-name">
                                    {token?.symbol === 'BTC' ? 'Bitcoin' :
                                      token?.symbol === 'ETH' ? 'Ethereum' :
                                        token?.symbol === 'BNB' ? 'Binance Coin' :
                                          token?.symbol === 'XRP' ? 'Ripple' :
                                            token?.symbol === 'SOL' ? 'Solana' : token?.symbol}
                                  </small>
                                </div>
                              </div>
                              <div className="token-right">
                                <div className="token-price">
                                  ${Number(token?.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </div>
                                <div className={`token-change ${parseFloat(token?.quantity || 0) > 0 ? 'positive' : 'negative'}`}>
                                  {parseFloat(token?.quantity || 0) > 0 ? 
                                    `+${parseFloat(token?.quantity || 0).toFixed(2)}%` : 
                                    `${parseFloat(token?.quantity || 0).toFixed(2)}%`
                                  }
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="modern-card modern-news-card" style={{minHeight: '500px'}}>
              <Card.Body>
                <div className="card-header-modern">
                  <h5 className="card-title-modern">
                    <Globe size={20} className="me-2" />
                    Latest News
                  </h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="modern-btn-sm"
                    onClick={() => window.open('https://cointelegraph.com/category/latest-news', '_blank')}
                  >
                    <Globe size={14} className="me-1" />
                    View All
                  </Button>
                </div>

                <div className="modern-news-list">
                  {news?.length > 0 ? (
                    news.map((article, index) => (
                      <div key={index} className="modern-news-item">
                        <a
                          href={article.link?.[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="modern-news-link"
                        >
                          <div className="news-content">
                            <h6 className="news-title">{article.title?.[0]}</h6>
                            <small className="news-time">
                              {article?.pubDate?.[0] ? new Date(article.pubDate[0]).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'Unknown date'}
                            </small>
                          </div>
                        </a>
                      </div>
                    ))
                  ) : (
                    <div className="news-loading">
                      <p>Loading news...</p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* PnL Chart */}

          <Row className="mb-4">
            <Col>
              <Card className="modern-card">
                <Card.Body>
                  <div className="card-header-modern">
                    <h5 className="card-title-modern">
                      <TrendingUp size={20} className="me-2" />
                      Bot Performance
                    </h5>
                  </div>
                  <PnlHistoryChart onTotalPnlChange={handleTotalPnlChange} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

      </Container>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    botHistoryData: state.botReducer.botHistoryData,
    botStatisticsData: state.botReducer.botStatisticsData,
    botHistorySortData: state.botReducer.botHistorySortData,
    isLoading: state.loadingReducer.isLoading,
    user: state.userReducer.user,
    newsData: state.newsReducer.newsData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    accountDetailsRequest: (payload) => {
      dispatch(accountDetailsRequestAction(payload));
    },
    getPnlHistoryRequestAction: (creds) => {
      dispatch(getPnlHistoryRequestAction(creds));
    },
    getNewsRequest: (payload) => {
      dispatch(getNewsRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserHome);